import { X } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

const MAX_DURATION = 60 // seconds

interface AudioVisualizerProps {
  duration: number
  getFrequencyData: () => Uint8Array
  onCancel: () => void
  className?: string
}

export function AudioVisualizer({
  duration,
  getFrequencyData,
  onCancel,
  className,
}: AudioVisualizerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const animationRef = useRef<number | null>(null)
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 })

  // Calculate progress state
  const remaining = MAX_DURATION - duration
  const isWarning = remaining <= 15
  const isCritical = remaining <= 5

  // Format duration as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
  }

  // Handle resize
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect
        setCanvasSize({ width, height })
      }
    })

    resizeObserver.observe(container)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  // Animation loop
  useEffect(() => {
    if (canvasSize.width === 0) return

    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const dpr = window.devicePixelRatio || 1

    const draw = () => {
      const frequencyData = getFrequencyData()
      const width = canvasSize.width
      const height = canvasSize.height

      // Clear canvas
      ctx.clearRect(0, 0, width * dpr, height * dpr)

      // Scale context for high-DPI
      ctx.save()
      ctx.scale(dpr, dpr)

      // Responsive bar count - fewer bars on mobile for cleaner look
      const minBarWidth = 4
      const gap = 2
      const maxBars = Math.floor(width / (minBarWidth + gap))
      const barCount = Math.min(maxBars, 48)
      const totalGapWidth = (barCount - 1) * gap
      const barWidth = (width - totalGapWidth) / barCount
      const centerY = height / 2

      for (let i = 0; i < barCount; i++) {
        // Sample frequency data - focus on voice frequencies
        const dataIndex = Math.floor(
          (i / barCount) * (frequencyData.length * 0.5),
        )
        const value = frequencyData[dataIndex] ?? 0
        const normalizedValue = value / 255

        // Smooth amplitude with minimum height
        const amplitude = Math.max(0.08, normalizedValue)
        const barHeight = amplitude * (height * 0.9)

        const x = i * (barWidth + gap)
        const y = centerY - barHeight / 2

        // Color based on progress state
        let baseColor: string
        if (isCritical) {
          baseColor = `rgba(239, 68, 68, ${0.6 + normalizedValue * 0.4})` // red
        } else if (isWarning) {
          baseColor = `rgba(245, 158, 11, ${0.6 + normalizedValue * 0.4})` // amber
        } else {
          baseColor = `rgba(16, 185, 129, ${0.6 + normalizedValue * 0.4})` // emerald
        }

        ctx.fillStyle = baseColor
        ctx.beginPath()
        ctx.roundRect(x, y, barWidth, barHeight, Math.min(barWidth / 2, 2))
        ctx.fill()
      }

      ctx.restore()
      animationRef.current = requestAnimationFrame(draw)
    }

    draw()

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [getFrequencyData, canvasSize, isWarning, isCritical])

  const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1

  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl border bg-slate-50 dark:bg-slate-800/50",
        "animate-in slide-in-from-bottom-2 duration-200",
        isCritical
          ? "border-red-300 dark:border-red-800"
          : isWarning
            ? "border-amber-300 dark:border-amber-800"
            : "border-slate-200 dark:border-slate-700",
        className,
      )}
    >
      {/* Main content - responsive padding and gaps */}
      <div className="flex items-center gap-2 px-3 py-2.5 sm:gap-4 sm:px-4 sm:py-3">
        {/* Recording indicator */}
        <div className="flex shrink-0 items-center">
          <span className="relative flex size-2 sm:size-2.5">
            <span
              className={cn(
                "absolute inline-flex size-full animate-ping rounded-full opacity-75",
                isCritical
                  ? "bg-red-500"
                  : isWarning
                    ? "bg-amber-500"
                    : "bg-red-500",
              )}
            />
            <span
              className={cn(
                "relative inline-flex size-2 rounded-full sm:size-2.5",
                isCritical
                  ? "bg-red-500"
                  : isWarning
                    ? "bg-amber-500"
                    : "bg-red-500",
              )}
            />
          </span>
        </div>

        {/* Waveform visualization - responsive height */}
        <div ref={containerRef} className="h-8 min-w-0 flex-1 sm:h-10">
          <canvas
            ref={canvasRef}
            width={canvasSize.width * dpr}
            height={canvasSize.height * dpr}
            style={{
              width: canvasSize.width || "100%",
              height: canvasSize.height || "100%",
            }}
            aria-hidden="true"
          />
        </div>

        {/* Timer - responsive text size */}
        <div className="flex shrink-0 flex-col items-end">
          <span
            className={cn(
              "font-mono text-xs font-semibold tabular-nums sm:text-sm",
              isCritical
                ? "text-red-600 dark:text-red-400"
                : isWarning
                  ? "text-amber-600 dark:text-amber-400"
                  : "text-slate-700 dark:text-slate-300",
            )}
          >
            {formatTime(duration)}
          </span>
          {isWarning && (
            <span className="text-[9px] font-medium text-slate-500 tabular-nums sm:text-[10px] dark:text-slate-400">
              {remaining}s left
            </span>
          )}
        </div>

        {/* Cancel button - proper touch target (min 44px on mobile) */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onCancel}
          className="size-9 shrink-0 rounded-full text-slate-400 hover:bg-slate-200 hover:text-slate-600 sm:size-8 dark:hover:bg-slate-700 dark:hover:text-slate-300"
          aria-label="Cancel recording"
        >
          <X className="size-4" />
        </Button>
      </div>

      {/* Progress bar - uses CSS animation for smooth 60s fill */}
      <div className="h-1 w-full bg-slate-200 dark:bg-slate-700">
        <div
          className={cn(
            "h-full origin-left",
            isCritical
              ? "bg-red-500"
              : isWarning
                ? "bg-amber-500"
                : "bg-emerald-500",
          )}
          style={{
            animation: `progress-fill ${MAX_DURATION}s linear forwards`,
          }}
          role="progressbar"
          aria-valuenow={duration}
          aria-valuemin={0}
          aria-valuemax={MAX_DURATION}
          aria-label={`Recording time: ${duration} of ${MAX_DURATION} seconds`}
        />
        <style>{`
          @keyframes progress-fill {
            from { width: 0%; }
            to { width: 100%; }
          }
        `}</style>
      </div>
    </div>
  )
}
